import React, { useState, useEffect, useRef, useCallback } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';


// -------------------------------------------------------------------
// Engine config
// -------------------------------------------------------------------
const ENGINE_CONFIG = {
  tactical: {
    title: 'Tactical Battlemap Generator Library',
    desc: 'Browse and access all tactical battlemap generators.',
    color: '#D4AF37',
    gradient: 'linear-gradient(135deg, #D4AF37, #b8962e)',
    domain: 'battlemap-tactical',
    group: 'tactical-battlemaps',
    backRoute: '/battlemaps/tactical',
    categories: [
      { id: 'dungeon',     name: 'Dungeon Maps',            icon: '🏰', desc: 'Underground dungeon layouts, corridors and chamber configurations.' },
      { id: 'wilderness',  name: 'Wilderness Encounters',   icon: '🌲', desc: 'Forest clearings, open fields and wilderness encounter zones.' },
      { id: 'castle',      name: 'Castle Interiors',        icon: '🗡️', desc: 'Throne rooms, guard barracks, towers and castle interior spaces.' },
      { id: 'cave',        name: 'Cave Systems',            icon: '🪨', desc: 'Natural cave networks, underground rivers and cavern formations.' },
      { id: 'ruins',       name: 'Ancient Ruins',           icon: '🏛️', desc: 'Collapsed structures, overgrown ruins and ancient site layouts.' },
      { id: 'town',        name: 'Town & Settlement',       icon: '🏘️', desc: 'Town squares, market districts, alleyways and settlement layouts.' },
      { id: 'tavern',      name: 'Tavern & Inn',            icon: '🍺', desc: 'Common rooms, upper floors, kitchens and inn floor plans.' },
      { id: 'temple',      name: 'Temple & Shrine',         icon: '⛩️', desc: 'Sacred spaces, altar rooms, crypts and religious site layouts.' },
      { id: 'sewer',       name: 'Sewers & Undercity',      icon: '🌊', desc: 'Underground waterways, sewer tunnels and undercity passages.' },
      { id: 'ship',        name: 'Ship & Vessel',           icon: '⛵', desc: 'Deck layouts, cargo holds, cabins and naval vessel maps.' },
      { id: 'campsite',    name: 'Camp & Outpost',          icon: '⛺', desc: 'Military camps, ranger outposts and temporary fortifications.' },
      { id: 'crossroads',  name: 'Road & Crossroads',       icon: '🛣️', desc: 'Road intersections, waypoints and travel encounter locations.' },
    ],
  },
  structures: {
    title: 'Structure Asset Generator Library',
    desc: 'Browse and access all structure and building asset generators.',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    domain: 'battlemap-structures',
    group: 'structure-assets',
    backRoute: '/battlemaps/structures',
    categories: [
      { id: 'buildings',   name: 'Buildings',               icon: '🏠', desc: 'Standalone buildings, houses and residential structures.' },
      { id: 'towers',      name: 'Towers',                  icon: '🗼', desc: 'Guard towers, mage towers, bell towers and vertical structures.' },
      { id: 'bridges',     name: 'Bridges',                 icon: '🌉', desc: 'Stone bridges, rope bridges, drawbridges and crossing structures.' },
      { id: 'walls',       name: 'Walls & Fortifications',  icon: '🧱', desc: 'Defensive walls, battlements, gates and fortification segments.' },
      { id: 'docks',       name: 'Docks & Piers',           icon: '🚢', desc: 'Harbour docks, fishing piers, boathouses and waterfront structures.' },
      { id: 'stables',     name: 'Stables & Barns',         icon: '🐴', desc: 'Animal enclosures, stables, barns and agricultural structures.' },
      { id: 'mills',       name: 'Mills & Workshops',       icon: '⚙️', desc: 'Windmills, watermills, forges and craftsman workshops.' },
      { id: 'shrines',     name: 'Shrines & Monuments',     icon: '🗿', desc: 'Small shrines, standing stones, monuments and markers.' },
    ],
  },
  props: {
    title: 'Prop Asset Generator Library',
    desc: 'Browse and access all prop and encounter object generators.',
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    domain: 'battlemap-props',
    group: 'props-objects',
    backRoute: '/battlemaps/props',
    categories: [
      { id: 'crates',      name: 'Crates & Containers',     icon: '📦', desc: 'Wooden crates, barrels, chests and storage containers.' },
      { id: 'barrels',     name: 'Barrels & Kegs',          icon: '🛢️', desc: 'Ale barrels, powder kegs, oil drums and cylindrical containers.' },
      { id: 'altars',      name: 'Altars & Pedestals',      icon: '🕯️', desc: 'Stone altars, offering pedestals, ritual surfaces and plinths.' },
      { id: 'furniture',   name: 'Furniture',               icon: '🪑', desc: 'Tables, chairs, beds, shelves and interior furnishings.' },
      { id: 'traps',       name: 'Traps & Hazards',         icon: '⚠️', desc: 'Floor traps, pressure plates, spike pits and encounter hazards.' },
      { id: 'treasure',    name: 'Treasure & Loot',         icon: '💰', desc: 'Treasure piles, gold coins, gemstones and loot objects.' },
      { id: 'weapons',     name: 'Weapon Racks',            icon: '⚔️', desc: 'Weapon stands, sword racks, shield displays and armament storage.' },
      { id: 'campfire',    name: 'Campfires & Light',       icon: '🔥', desc: 'Campfires, torches, lanterns and light source objects.' },
      { id: 'statues',     name: 'Statues & Decor',         icon: '🗿', desc: 'Decorative statues, busts, columns and ornamental props.' },
      { id: 'plants',      name: 'Plants & Vegetation',     icon: '🌿', desc: 'Indoor plants, vines, mushrooms and decorative vegetation.' },
    ],
  },
  terrain: {
    title: 'Terrain Tile Generator Library',
    desc: 'Browse and access all terrain tile and environment generators.',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981, #059669)',
    domain: 'battlemap-terrain',
    group: 'terrain-tiles',
    backRoute: '/battlemaps/terrain',
    categories: [
      { id: 'grass',       name: 'Grass Tiles',             icon: '🌾', desc: 'Meadow grass, plains, pasture and light terrain tile sets.' },
      { id: 'desert',      name: 'Desert Tiles',            icon: '🏜️', desc: 'Sand dunes, arid rock, desert scrub and dry terrain tiles.' },
      { id: 'lava',        name: 'Lava Tiles',              icon: '🌋', desc: 'Lava flows, volcanic rock, obsidian and fire terrain tiles.' },
      { id: 'snow',        name: 'Snow & Ice Tiles',        icon: '❄️', desc: 'Snow cover, frozen lakes, tundra and cold biome terrain tiles.' },
      { id: 'forest',      name: 'Forest Tiles',            icon: '🌲', desc: 'Dense forest, woodland, jungle canopy and tree coverage tiles.' },
      { id: 'water',       name: 'Water Tiles',             icon: '🌊', desc: 'Rivers, lakes, ocean shallows and water terrain tile sets.' },
      { id: 'swamp',       name: 'Swamp & Marsh Tiles',     icon: '🐸', desc: 'Bog water, marsh reeds, mudflats and wetland terrain tiles.' },
      { id: 'stone',       name: 'Stone & Rock Tiles',      icon: '🪨', desc: 'Flagstone, cobblestone, bare rock and stone surface tiles.' },
      { id: 'dirt',        name: 'Dirt & Earth Tiles',      icon: '🟫', desc: 'Packed earth, soil, muddy paths and bare ground tile sets.' },
      { id: 'cave',        name: 'Cave Floor Tiles',        icon: '🕳️', desc: 'Underground cave floors, stalactite zones and cavern tiles.' },
    ],
  },
};

// -------------------------------------------------------------------
// BattlemapGenerators — shared component for all 4 engine library pages
// -------------------------------------------------------------------
export default function BattlemapGenerators() {
  const { engine } = useParams();
  const navigate   = useNavigate();
  const { user, logout } = useAuth();

  const cfg = ENGINE_CONFIG[engine];

  // Redirect to dashboard if invalid engine
  useEffect(() => {
    if (!cfg) navigate('/dashboard/battlemaps');
  }, [cfg, navigate]);

  if (!cfg) return null;

  const displayName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email;
  const avatarLetter = ((user?.first_name || user?.email || 'V')[0]).toUpperCase();

  return (
    <BattlemapGeneratorPage
      key={engine}
      cfg={cfg}
      engine={engine}
      navigate={navigate}
      displayName={displayName}
      avatarLetter={avatarLetter}
      logout={logout}
    />
  );
}

function BattlemapGeneratorPage({ cfg, engine, navigate, displayName, avatarLetter, logout }) {
  const [view,         setView]         = useState('categories'); // 'categories' | 'results'
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [packs,        setPacks]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [page,         setPage]         = useState(1);
  const [hasMore,      setHasMore]      = useState(false);
  const [totalPacks,   setTotalPacks]   = useState(0);
  const [autoResults,  setAutoResults]  = useState({});
  const [showAuto,     setShowAuto]     = useState(false);
  const searchRef = useRef(null);
  const PACKS_PER_PAGE = 5;

  // Autocomplete is a bounded generator list. Full search is pack-first and
  // engine-grouped, with all totals and pagination supplied by the backend.
  const normalizeBattlemapsResults = useCallback((rows) => {
    return (rows || [])
      .filter(r => r && r.type === 'generator' && (!cfg.group || r.group === cfg.group))
      .map(r => ({ ...r, family: r.pack_name || r.group_name || '' }));
  }, [cfg.group]);

  const fetchGenerators = useCallback(async (query, cat, pg = 1) => {
    setLoading(true);
    setError('');
    try {
      // Old category-filter semantics map to a Battlemaps text query: the free
      // text takes precedence; otherwise the selected category name is the seed.
      const text  = (query && query.trim()) ? query.trim() : (cat ? cat.name : '');
      const q = text.trim();
      let nextPacks = [];
      let returnedTotal = 0;
      let returnedPages = 1;
      if (q) {
        const res = await API.get('/v1/battlemaps/search', {
          params: { q, engine: cfg.group, page: pg, per_page: PACKS_PER_PAGE, preview_per_engine: 48 },
        });
        nextPacks = res.data?.packs || [];
        returnedTotal = res.data?.total_packs || 0;
        returnedPages = res.data?.pages || 1;
      }
      setPacks(current => pg === 1 ? nextPacks : [...current, ...nextPacks]);
      setTotalPacks(returnedTotal);
      setHasMore(pg < returnedPages);
    } catch {
      if (pg === 1) {
        setPacks([]);
        setTotalPacks(0);
      }
      setHasMore(false);
      setError('Unable to load Battlemaps search results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [cfg.group]);

  const fetchAuto = useCallback(async (q) => {
    if (!q || q.length < 2) { setAutoResults({}); setShowAuto(false); return; }
    try {
      const res = await API.get('/v1/battlemaps/search/autocomplete', {
        params: { q: q.trim(), engine: cfg.group, limit: 8 },
      });
      const gens = normalizeBattlemapsResults(res.data?.results);
      setAutoResults(gens.length ? { generators: gens } : {});
      setShowAuto(gens.length > 0);
    } catch {
      setAutoResults({});
    }
  }, [normalizeBattlemapsResults, cfg.group]);

  useEffect(() => {
    const t = setTimeout(() => fetchAuto(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput, fetchAuto]);

  function handleCategoryClick(cat) {
    setActiveCategory(cat);
    setView('results');
    setPage(1);
    fetchGenerators('', cat, 1);
  }

  function handleSearchSubmit(e) {
    e && e.preventDefault();
    setShowAuto(false);
    setActiveCategory(null);
    setSearchQuery(searchInput);
    setView('results');
    setPage(1);
    fetchGenerators(searchInput, null, 1);
  }

  function handleLoadMore() {
    const next = page + 1;
    setPage(next);
    fetchGenerators(searchQuery, activeCategory, next);
  }

  function handleBackToCategories() {
    setView('categories');
    setActiveCategory(null);
    setSearchQuery('');
    setSearchInput('');
    setPacks([]);
    setError('');
  }

  function handleGeneratorClick(gen) {
    const slug = gen.slug || gen.id;
    if (slug) navigate('/engine', { state: { slug } });
  }

  const breadcrumb = activeCategory ? activeCategory.name : (searchQuery ? `"${searchQuery}"` : '');

  return (
    <div style={s.page}>

      <nav style={s.nav}>
        <div style={s.navLogo} onClick={() => navigate('/dashboard/battlemaps')}>Visinvoke</div>
        <div style={s.navRight}>
          <button style={{ ...s.navBtn, color: cfg.color, borderColor: `${cfg.color}55` }}
            onClick={() => navigate(cfg.backRoute)}>
            &larr; {cfg.title.split(' ')[0]} Engine
          </button>

          <div style={s.avatar}>{avatarLetter}</div>
          <span style={{ fontSize: 14, color: '#cbd5e1' }}>{displayName}</span>
          <button style={s.navBtn} onClick={() => { logout(); navigate('/login'); }}>Sign Out</button>
        </div>
      </nav>

      <div className="vi-desktop-wide" style={s.container}>

        {/* Library header */}
        <div style={s.libHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ ...s.engineIcon, background: cfg.gradient }}>
              {engine === 'tactical' ? '🗺️' : engine === 'structures' ? '🏰' : engine === 'props' ? '⚗️' : '🌿'}
            </div>
            <div>
              <h1 style={s.libTitle}>{cfg.title}</h1>
              <p style={s.libSub}>{cfg.desc}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={s.searchWrap} ref={searchRef}>
          <form style={s.searchForm} onSubmit={handleSearchSubmit}>
            <input
              style={s.searchInput}
              placeholder={`Search ${engine} generators...`}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => { if (Object.keys(autoResults).length) setShowAuto(true); }}
              autoComplete="off"
            />
            <button type="submit" style={{ ...s.searchBtn, background: cfg.gradient }}>Search</button>
          </form>
          {showAuto && Object.keys(autoResults).length > 0 && (
            <div style={s.autoList}>
              {autoResults.generators?.length > 0 && (
                <>
                  <div style={s.autoGroupLabel}>Generators</div>
                  {autoResults.generators.map((r, i) => (
                    <div key={i} style={s.autoItem} onClick={() => { setSearchInput(r.name || r.slug); setShowAuto(false); handleGeneratorClick(r); }}>
                      <span style={{ fontWeight: 600 }}>{r.name || r.slug}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* View: Categories */}
        {view === 'categories' && (
          <div>
            <div style={s.viewHeader}>
              <div>
                <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 6px', color: '#fff' }}>{cfg.title}</h1>
                <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>{cfg.desc}</p>
              </div>
            </div>
            <div style={s.categoryGrid}>
              {cfg.categories.map((cat, i) => (
                <div key={i}
                  style={s.categoryTile}
                  onClick={() => handleCategoryClick(cat)}
                  onMouseEnter={e => {
                    e.currentTarget.style.background  = `${cfg.color}22`;
                    e.currentTarget.style.borderColor = `${cfg.color}88`;
                    e.currentTarget.style.transform   = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background  = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.transform   = 'none';
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <span style={{ fontSize: 30 }}>{cat.icon}</span>
                    <span style={{ ...s.liveBadge, background: `${cfg.color}22`, color: cfg.color, borderColor: `${cfg.color}44` }}>Live</span>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: '#fff' }}>{cat.name}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: '#94a3b8' }}>{cat.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View: Results */}
        {view === 'results' && (
          <div>
            <div style={s.resultsHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button style={s.backBtn} onClick={handleBackToCategories}>&larr; Categories</button>
                <h2 style={s.resultsTitle}>{breadcrumb}</h2>
              </div>
              {!loading && <span style={{ color: '#94a3b8', fontSize: 13 }}>{totalPacks.toLocaleString()} environment pack{totalPacks === 1 ? '' : 's'}</span>}
            </div>

            {error && <div style={s.errorMsg}>{error}</div>}
            {loading && packs.length === 0 ? (
              <div style={s.loadingMsg}>Loading generators...</div>
            ) : packs.length === 0 && !error ? (
              <div style={s.emptyMsg}>No generators found.</div>
            ) : packs.length > 0 ? (
              <>
                {packs.map(pack => {
                  const group = pack.groups?.[cfg.group];
                  const generators = group?.results || [];
                  return (
                    <section key={pack.pack_slug} style={s.packSection}>
                      <div style={s.packHeader}>
                        <div>
                          <h3 style={s.packTitle}>{pack.pack_name}</h3>
                          <div style={s.packMeta}>{pack.total_matches.toLocaleString()} matching generator{pack.total_matches === 1 ? '' : 's'}</div>
                        </div>
                        <span style={pack.owned ? s.ownedBadge : s.notOwnedBadge}>{pack.owned ? 'Owned' : 'Not Owned'}</span>
                      </div>
                      {group && (
                        <div style={s.engineGroupHeader}>{group.engine_name} · {group.total.toLocaleString()} matches</div>
                      )}
                      <div style={s.genGrid}>
                        {generators.map(gen => (
                          <div key={gen.slug} style={s.genCard}
                            onClick={() => handleGeneratorClick(gen)}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = cfg.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'none'; }}>
                            <div style={s.genName}>{gen.name || gen.slug}</div>
                            <div style={s.genFamily}>{pack.pack_name}</div>
                            <button style={{ ...s.genBtn, background: cfg.gradient }}>Open</button>
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                })}
                {hasMore && (
                  <div style={{ textAlign: 'center', marginTop: 32 }}>
                    <button style={{ ...s.loadMoreBtn, borderColor: cfg.color, color: cfg.color }}
                      onClick={handleLoadMore} disabled={loading}>
                      {loading ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

      </div>
    </div>
  );
}

const s = {
  page:      { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif" },
  container: { maxWidth: 1400, margin: '0 auto', padding: '28px 32px' },

  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px', background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)', height: 60, position: 'sticky', top: 0, zIndex: 200, gap: 16 },
  navLogo: { fontWeight: 900, fontSize: 20, cursor: 'pointer', letterSpacing: '-0.5px', background: 'linear-gradient(90deg, #a78bfa, #f472b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', flexShrink: 0 },
  navRight: { display: 'flex', alignItems: 'center', gap: 10 },
  navBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  avatar: { width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 },

  libHeader: { marginBottom: 24 },
  engineIcon: { width: 52, height: 52, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 },
  libTitle: { color: '#fff', fontSize: 26, fontWeight: 800, margin: '0 0 4px' },
  libSub: { color: '#94a3b8', fontSize: 14, margin: 0 },

  searchWrap: { position: 'relative', marginBottom: 32 },
  searchForm: { display: 'flex', gap: 0 },
  searchInput: { flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRight: 'none', borderRadius: '10px 0 0 10px', padding: '12px 18px', fontSize: 15, color: '#fff', outline: 'none' },
  searchBtn: { color: '#fff', border: 'none', borderRadius: '0 10px 10px 0', padding: '12px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  autoList: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e1b4b', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '0 0 10px 10px', zIndex: 100, maxHeight: 320, overflowY: 'auto' },
  autoGroupLabel: { padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)' },
  autoItem: { padding: '9px 16px', fontSize: 14, color: 'rgba(255,255,255,0.8)', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' },

  viewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  categoryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  categoryTile: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, padding: '20px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, transform 0.15s' },
  liveBadge: { fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '2px 7px', borderRadius: 10, border: '1px solid', whiteSpace: 'nowrap' },

  resultsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 },
  resultsTitle: { fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 },
  backBtn: { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },

  packSection: { marginBottom: 28, padding: 18, borderRadius: 14, background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.09)' },
  packHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  packTitle: { margin: 0, color: '#fff', fontSize: 19, fontWeight: 750 },
  packMeta: { marginTop: 5, color: '#94a3b8', fontSize: 12 },
  engineGroupHeader: { marginBottom: 12, color: '#cbd5e1', fontSize: 13, fontWeight: 650 },
  ownedBadge: { color: '#6ee7b7', border: '1px solid rgba(52,211,153,0.45)', background: 'rgba(52,211,153,0.12)', borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 700 },
  notOwnedBadge: { color: '#fbbf24', border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.1)', borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 700 },

  loadingMsg: { textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.4)', fontSize: 15 },
  emptyMsg:   { textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.4)', fontSize: 15 },
  errorMsg:   { color: '#fecaca', border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(127,29,29,0.22)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 },

  genGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 },
  genCard: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px', cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s', display: 'flex', flexDirection: 'column', gap: 8 },
  genName: { fontWeight: 700, fontSize: 13, color: '#fff', lineHeight: 1.35, flex: 1 },
  genFamily: { fontSize: 11, color: '#94a3b8' },
  genBtn: { color: '#fff', border: 'none', borderRadius: 6, padding: '7px 0', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginTop: 'auto' },

  loadMoreBtn: { background: 'transparent', border: '2px solid', borderRadius: 10, padding: '11px 32px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
};
